import { Bell, Building2, Car, LogOut, ShieldCheck, User, UserRound } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Link } from 'react-router-dom';

import { PAPEIS_EXTERNOS, rotuloDoPapel } from '@/nucleo/auth/papeis';
import { type UsuarioAutenticado } from '@/nucleo/auth/tipos';
import { Avatar, AvatarFallback } from '@/ui/base/avatar';
import { Badge } from '@/ui/base/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/base/dropdown-menu';

import { iniciais } from './apresentacao';
import { AREAS, AREA_SME, type AreaDeNavegacao } from './mapa';

/**
 * Botão + rótulo do trigger, iguais nas duas versões do menu (SME e geral).
 *
 * `forwardRef` + `...resto` são obrigatórios aqui: `DropdownMenuTrigger
 * asChild` (Radix `Slot`) injeta `ref`, `onClick`, `aria-expanded` etc. no
 * filho direto — sem repassar, o clique nunca abre o menu.
 */
const GatilhoDaConta = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<'button'> & {
    usuario: UsuarioAutenticado;
    papelPrincipal: string | null;
  }
>(function GatilhoDaConta({ usuario, papelPrincipal, ...resto }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className="flex items-center gap-2.5 rounded-xl px-2 py-1.5 outline-hidden transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring"
      // O nome não cabe em 375 px, então o rótulo acessível carrega a
      // identificação inteira, e a tela mostra só as iniciais.
      aria-label={`Conta de ${usuario.nome ?? 'usuário'}${
        papelPrincipal ? `, ${rotuloDoPapel(papelPrincipal)}` : ''
      }`}
      {...resto}
    >
      <div className="hidden min-w-0 flex-col items-end gap-0.5 sm:flex">
        <p className="max-w-[140px] truncate text-sm font-medium leading-tight">
          {usuario.nome ?? 'Conta'}
        </p>
        {papelPrincipal && (
          <Badge
            variant="secondary"
            className="h-5 whitespace-nowrap px-1.5 py-0 text-[10px] font-medium"
          >
            {rotuloDoPapel(papelPrincipal)}
          </Badge>
        )}
      </div>

      <Avatar className="h-11 w-11 shrink-0">
        <AvatarFallback className="bg-primary text-sm font-semibold text-primary-foreground">
          {iniciais(usuario.nome)}
        </AvatarFallback>
      </Avatar>
    </button>
  );
});

/**
 * Menu da conta: quem está conectado, com que perfil, e a opção de sair.
 *
 * docs/PROMPTS-CLAUDE.md, Prompt 6: "menu da conta com a opção de sair".
 *
 * O que aparece aqui sai todo do GET /eu. Não há troca de perfil nem de persona:
 * papel é atribuição do Keycloak, sincronizada para o banco a cada entrada
 * (docs/AUTENTICACAO-E-ACESSO.md, seção 5). Trocar de papel na tela seria
 * simulação — e simulação de acesso é justamente o que não entra neste projeto.
 *
 * Quem alcança outra área (DTP, Admin, SME, DRE) ganha o atalho pra ela.
 * Só aparece a que o usuário de fato alcança: o que o usuário não pode fazer
 * não é renderizado (CLAUDE.md, "Regras de interface", item 7). Deriva de
 * `AREAS`, não de um par fixo, porque com SME e DRE existindo (H-102.A2) pode
 * haver mais de uma área alcançável além da atual.
 */
export function MenuDaConta({
  usuario,
  areaAtual,
  aoSair,
}: {
  usuario: UsuarioAutenticado;
  areaAtual: AreaDeNavegacao;
  aoSair: () => void;
}) {
  const papelPrincipal = usuario.papelPrincipal ?? usuario.papeis[0] ?? null;

  // SME tem um menu de conta PRÓPRIO no protótipo (`SmeLayout.tsx`), bem mais
  // simples que o genérico (`AppLayout.tsx`, usado aqui pras demais áreas):
  // sem badge de segundo fator, sem os atalhos "Ir para X" pra outras áreas —
  // só "Voltar ao Painel Admin" (quando a conta é admin), "Portal do
  // Credenciado", "Meu perfil" e "Preferências de notificação" (esta última
  // não existe no protótipo, entrou por decisão do dono em 2026-09-24).
  if (areaAtual.raiz === '/sme') {
    return <MenuDaContaSme usuario={usuario} papelPrincipal={papelPrincipal} aoSair={aoSair} />;
  }

  // Portal do Credenciado também tem menu próprio no protótipo
  // (`PortalLayout.tsx`): "Meu perfil" (desde 05/10/2026 a tela própria,
  // `/credenciado/perfil`), "Preferências de notificação", mais o atalho
  // condicional "Portal SME", que não existe no genérico. O "Voltar ao Painel
  // Admin" do protótipo não vem: papel interno não entra no Portal.
  if (areaAtual.raiz === '/credenciado') {
    return (
      <MenuDaContaCredenciado usuario={usuario} papelPrincipal={papelPrincipal} aoSair={aoSair} />
    );
  }

  const outrasAreasAlcancaveis = AREAS.filter(
    (area) =>
      area.raiz !== areaAtual.raiz && area.papeis.some((papel) => usuario.papeis.includes(papel)),
  );
  // `canAdmin` do protótipo (`AppLayout.tsx:103`): ADMIN ou AUDITOR — não
  // GESTOR_ADMIN. Controla os dois links abaixo lá; aqui só sobra o de
  // Portal do Credenciado (o de "Administração" já vem de
  // `administracaoAlcancavel`, com o RBAC real da área).
  const podeVerPortalCredenciado =
    usuario.papeis.includes('ADMIN') || usuario.papeis.includes('AUDITOR');
  const administracaoAlcancavel = outrasAreasAlcancaveis.find((area) => area.raiz === '/admin');
  // `canSme` do protótipo (`AppLayout.tsx:104`): alcançável por quem tem
  // qualquer papel de SME ou DRE, além de ADMIN/GESTOR_ADMIN — o mesmo
  // `PAPEIS_SME`, então a checagem de área já cobre. O rótulo lá é fixo
  // ("Portal SME"), não o nome genérico da área.
  const smeAlcancavel = outrasAreasAlcancaveis.find((area) => area.raiz === '/sme');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <GatilhoDaConta usuario={usuario} papelPrincipal={papelPrincipal} />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="space-y-1">
          <p className="truncate text-sm font-semibold">{usuario.nome ?? 'Usuário'}</p>
          {usuario.email && (
            <p className="truncate text-xs font-normal text-muted-foreground">{usuario.email}</p>
          )}
          {papelPrincipal && (
            <p className="flex items-center gap-1.5 pt-1 text-xs font-normal text-muted-foreground">
              <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
              {rotuloDoPapel(papelPrincipal)}
            </p>
          )}
          {usuario.mfaVerificada && (
            <p className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-success" aria-hidden="true" />
              Segundo fator confirmado
            </p>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <ItensDaConta raiz={areaAtual.raiz} />

        <DropdownMenuSeparator />

        {administracaoAlcancavel && (
          <>
            <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
              <Link to={administracaoAlcancavel.raiz}>
                <administracaoAlcancavel.icone className="mr-2 h-4 w-4" aria-hidden="true" />
                Administração
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/*
          "Portal do Credenciado" no protótipo dava acesso a uma visão de
          outro perfil — aqui é só a rota pública real (`/`), sem simular
          papel nenhum (CLAUDE.md proíbe troca de persona). Visível só pra
          quem tem `canAdmin` lá (ADMIN ou AUDITOR) — não é atalho pra todo
          mundo.
        */}
        {podeVerPortalCredenciado && (
          <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
            <Link to="/">
              <Car className="mr-2 h-4 w-4" aria-hidden="true" />
              Portal do credenciado
            </Link>
          </DropdownMenuItem>
        )}
        {smeAlcancavel && (
          <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
            <Link to={smeAlcancavel.raiz}>
              <smeAlcancavel.icone className="mr-2 h-4 w-4" aria-hidden="true" />
              Portal SME
            </Link>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={aoSair}
          className="cursor-pointer rounded-lg text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Menu de conta de SME, igual ao protótipo (`SmeLayout.tsx`) exceto por
 * "Preferências de notificação" (decisão do dono em 2026-09-24, ver abaixo):
 * rótulo "Portal SME" em vez do papel repetido, sem badge de segundo fator,
 * sem os atalhos "Ir para X" pra outras áreas — só "Voltar ao Painel Admin"
 * (quando a conta também é admin) e "Portal do Credenciado" (quando é admin
 * ou papel de credenciado).
 */
function MenuDaContaSme({
  usuario,
  papelPrincipal,
  aoSair,
}: {
  usuario: UsuarioAutenticado;
  papelPrincipal: string | null;
  aoSair: () => void;
}) {
  // `isAdmin` do protótipo (`SmeLayout.tsx`): ADMIN ou GESTOR_ADMIN.
  const ehAdmin = usuario.papeis.includes('ADMIN') || usuario.papeis.includes('GESTOR_ADMIN');
  const ehCredenciado = usuario.papeis.some((papel) =>
    (PAPEIS_EXTERNOS as readonly string[]).includes(papel),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <GatilhoDaConta usuario={usuario} papelPrincipal={papelPrincipal} />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="space-y-1">
          <p className="truncate text-sm font-semibold">{usuario.nome ?? 'Usuário'}</p>
          {usuario.email && (
            <p className="truncate text-xs font-normal text-muted-foreground">{usuario.email}</p>
          )}
          <p className="truncate text-xs font-normal text-muted-foreground">Portal SME</p>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <ItensDaConta raiz={AREA_SME.raiz} />

        {(ehAdmin || ehCredenciado) && <DropdownMenuSeparator />}
        {ehAdmin && (
          <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
            <Link to="/admin">
              <ShieldCheck className="mr-2 h-4 w-4" aria-hidden="true" />
              Voltar ao Painel Admin
            </Link>
          </DropdownMenuItem>
        )}
        {(ehCredenciado || ehAdmin) && (
          <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
            {/*
              Mesma adaptação da versão geral: no protótipo dava acesso a uma
              visão de outro perfil, aqui é só a rota pública real, sem
              simular papel nenhum (CLAUDE.md proíbe troca de persona).
            */}
            <Link to="/">
              <Car className="mr-2 h-4 w-4" aria-hidden="true" />
              Portal do credenciado
            </Link>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={aoSair}
          className="cursor-pointer rounded-lg text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Menu de conta do Portal do Credenciado, igual ao protótipo
 * (`PortalLayout.tsx`): rótulo de papel em texto simples (sem ícone, sem
 * badge de segundo fator — nenhum dos dois existe lá), "Meu perfil" e
 * "Preferências de notificação" sempre habilitados, e o atalho condicional
 * "Portal SME" pra quem também alcança SME.
 *
 * O "Voltar ao Painel Admin" do protótipo (`PortalLayout.tsx:239`) não vem:
 * papel interno não entra no Portal do Credenciado (decisão do dono em
 * 2026-10-02, `AREA_CREDENCIADO.papeis`), e esta casca é só de quem tem
 * papel externo.
 */
function MenuDaContaCredenciado({
  usuario,
  papelPrincipal,
  aoSair,
}: {
  usuario: UsuarioAutenticado;
  papelPrincipal: string | null;
  aoSair: () => void;
}) {
  const podeVerSme = usuario.papeis.some((papel) => AREA_SME.papeis.includes(papel));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <GatilhoDaConta usuario={usuario} papelPrincipal={papelPrincipal} />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="space-y-1">
          <p className="truncate text-sm font-semibold">{usuario.nome ?? 'Usuário'}</p>
          {usuario.email && (
            <p className="truncate text-xs font-normal text-muted-foreground">{usuario.email}</p>
          )}
          {papelPrincipal && (
            <p className="truncate text-xs font-normal text-muted-foreground">
              {rotuloDoPapel(papelPrincipal)}
            </p>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <ItensDaConta raiz="/credenciado" />

        {podeVerSme && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
              <Link to="/sme">
                <Building2 className="mr-2 h-4 w-4" aria-hidden="true" />
                Portal SME
              </Link>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={aoSair}
          className="cursor-pointer rounded-lg text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * "Meu perfil" e "Preferências de notificação", NO MESMO LUGAR nos três menus
 * da conta — logo abaixo de quem está conectado, antes dos atalhos para outras
 * áreas e de "Sair" (decisão do dono, 05/10/2026, ordem M3). As duas telas
 * são montadas uma vez por área (`FORA_DO_MENU_POR_AREA` e
 * `ROTAS_DE_DETALHE_DO_CREDENCIADO`, em `App.tsx`), e `raiz` mantém a pessoa
 * dentro da casca em que ela já está.
 *
 * O Portal do Credenciado ganhou a própria tela (`/credenciado/perfil`): até
 * então, "Meu Perfil" levava à página inicial do portal, como no protótipo.
 */
function ItensDaConta({ raiz }: { raiz: string }) {
  return (
    <>
      <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
        <Link to={`${raiz}/perfil`}>
          <User className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
          Meu perfil
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
        <Link to={`${raiz}/perfil/notificacoes`}>
          <Bell className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
          Preferências de notificação
        </Link>
      </DropdownMenuItem>
    </>
  );
}
